// src/contexts/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import { useDispatch } from 'react-redux';

// Make sure SET_ACTIVE_USER and REMOVE_ACTIVE_USER are updated in authSlice.js
// to accept and handle the 'role' field as discussed in the previous step.
import { SET_ACTIVE_USER, REMOVE_ACTIVE_USER } from '../redux/slice/authSlice';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();

  useEffect(() => {
    console.log('[AuthContext] Effect running to set up auth listener.');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(`[AuthContext] onAuthStateChanged fired. User: ${firebaseUser ? firebaseUser.uid : 'null'}`);

      if (firebaseUser) {
        let userFirstName = null;
        let userLastName = null;
        let userRole = 'user'; // Initialize role to default 'user'
        const userEmail = firebaseUser.email;
        const userDocRef = doc(db, 'users', firebaseUser.uid);

        try {
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            userFirstName = userData.firstName || null;
            userLastName = userData.lastName || null;
            userRole = userData.role || 'user'; // Fetch role from Firestore, default to 'user'
            console.log(`[AuthContext] Fetched user document for UID ${firebaseUser.uid}: firstName=${userFirstName}, lastName=${userLastName}, role=${userRole}`);
          } else {
            console.warn(`[AuthContext] No user document found in Firestore for UID: ${firebaseUser.uid}. Assigning default role 'user'.`);
          }
        } catch (firestoreError) {
          console.error(`[AuthContext] Error fetching user document for UID ${firebaseUser.uid}:`, firestoreError);
          // If there's an error fetching, keep the role as default 'user'
        }

        // Fallback for displayName (if Firestore document didn't have name and Firebase Auth provided it)
        if (!userFirstName && !userLastName && firebaseUser.displayName) {
          const nameParts = firebaseUser.displayName.trim().split(' ').filter(p => p.length > 0);
          if (nameParts.length > 0) userFirstName = nameParts[0];
          if (nameParts.length > 1) userLastName = nameParts[nameParts.length - 1];
          console.log(`[AuthContext] Derived from firebase.displayName: firstName=${userFirstName}, lastName=${userLastName}`);
        }

        // Fallback for email (if no other name found)
        if (!userFirstName && !userLastName && userEmail) {
          const nameFromEmail = userEmail.substring(0, userEmail.indexOf("@"));
          userFirstName = nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1);
          userLastName = '';
          console.log(`[AuthContext] Derived from email: firstName=${userFirstName}, lastName=${userLastName}`);
        }


        dispatch(SET_ACTIVE_USER({
          email: userEmail,
          firstName: userFirstName,
          lastName: userLastName,
          userID: firebaseUser.uid,
          role: userRole, // <-- NOW INCLUDING THE USER'S ROLE!
        }));
        console.log(`[AuthContext] Dispatched SET_ACTIVE_USER for UID ${firebaseUser.uid} with firstName: ${userFirstName}, lastName: ${userLastName}, role: ${userRole}`);

      } else {
        console.log('[AuthContext] onAuthStateChanged: User logged out/no user.');
        dispatch(REMOVE_ACTIVE_USER());
        console.log('[AuthContext] Dispatched REMOVE_ACTIVE_USER.');
      }

      setLoading(false);
      console.log('[AuthContext] Auth check finished, setLoading(false).');
    });

    return () => {
      console.log('[AuthContext] Cleaning up onAuthStateChanged listener.');
      unsubscribe();
    };
  }, [dispatch]);


  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  };

  const logout = async () => {
    await signOut(auth);
  };


  const contextValue = {
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export { AuthContext };
